import { VRChairObject } from './VRChairObject';
import { VRChair } from './VRChair';
import { VRStreamScreenObject } from './VRStreamScreenObject';
import { VRSceneChangeObject } from './VRSceneChangeObject';
import { VRProfileObject } from './VRProfileObject';
import { VRLinkObject } from './VRLinkObject';
import { VRYoutubeGhostObject } from './VRYoutubeGhostObject';
import { VRYoutubeVideoObject } from './VRYoutubeVideoObject';
import { VRIframeAgentGhost } from './VRIframeAgentGhost';
import { VRIframeAgent } from './VRIframeAgent';
import { VRVapiCameraWithCSS3D } from './VRVapiCameraWithCSS3D';
import { VRVapiTranscriptionWithCSS3D } from './VRVapiTranscriptionWithCSS3D';
import { VRPayPalDonationObject } from './VRPayPalDonationObject';
import VRChessObject from './VRChessObject';
import { VRBaglessRoomObject } from './VRBaglessRoomObject';
import { VRShopObject } from './VRShopObject';
import { VRInfoElement } from './VRInfoElement';
import { VRInputFieldGuess } from './VRInputFieldGuess';
import { VRInvisibleAction } from './VRInvisibleAction';
import { VREmbeddedWebpageObject } from './VREmbeddedWebpageObject';
import { shareScreenGeometry } from './helpers/texturesAndGeometries';
import { VRScreenShare } from './VRScreenShare';
import { VRTourScreenShare } from './VRTourScreenShare';
import { VRImageGallery } from './VRImageGallery';
import { VRVideoGallery } from './VRVideoGallery';
import VRModel from './VRModel';
import { VRVideoPlayer } from './VRVideoPlayer';
import { VRVideoPlayerWebGL } from './VRVideoPlayerWebGL';
import { VRMusicObject } from './VRMusicObject';

const toNumberOrNull = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const normalizeDistanceAndSize = (obj) => {
    const distance = toNumberOrNull(obj?.distance);
    const size = toNumberOrNull(obj?.size);
    const scale = toNumberOrNull(obj?.scale);
    const hasLegacyDistanceInSize = distance === null && size !== null;

    const normalized = { ...obj };
    if (hasLegacyDistanceInSize) {
        normalized.distance = size;
    }

    const meshSize = hasLegacyDistanceInSize ? (scale ?? 1) : (size ?? scale ?? 1);
    normalized.size = meshSize;
    normalized.scale = meshSize;

    return { normalized, meshSize };
};

class VRPlaceObject {

    constructor(index, obj, texture, editMode = false, model) {
        const { normalized: normalizedObj, meshSize } = normalizeDistanceAndSize(obj || {});
        let element = null;
        const { id, type, name, x, y, z } = normalizedObj;
        let _Z = z || 90;

        switch (type) {
            case 'guestSeat':
                // element = new VRChairObject(index, null, texture, x, y, _Z, model);
                element = new VRChair(index, null, texture, x, y, _Z, model);
                break;
            case 'screenStream':
                if (model.editMode) {
                    element = new VRStreamScreenObject(index, null, texture, x, y, _Z, model);
                } else {
                    element = new VRScreenShare(index, null, texture, x, y, _Z, model);
                }
                element.mesh.type = 'screenStream';
                element.type = 'screenStream';
                element.name = 'screenStream';
                element.mesh.geometry = shareScreenGeometry;
                // element = new VRStreamScreenObject(index, null, texture, x, y, _Z, model);
                break;
            case 'sceneChange':
                element = new VRSceneChangeObject(index, null, texture, x, y, _Z, model);
                element.setTargetRoomId(obj.goToSceneId);
                break;
            case 'webLink':
                element = new VRLinkObject(index, null, texture, x, y, _Z, model);
                element.setLink(obj.url);
                break;
            case 'baglessRoom':
                element = new VRBaglessRoomObject(index, null, texture, x, y, _Z, model);
                element.setLink(obj.url);
                break;
            case 'youtube':
                if (editMode) {
                    // VRYoutubeGhost - must be renamed to VRYoutubePlaceholder
                    element = new VRYoutubeGhostObject(index, null, normalizedObj.video_id, x, y, _Z, model);
                } else {
                    element = new VRYoutubeVideoObject(null, normalizedObj.video_id, x, y, _Z);
                    element.scale.set(meshSize, meshSize, meshSize);
                    element.mesh.dbConfig = normalizedObj;
                    return element;
                }
                break;
            case 'iframeAgent':
                if (editMode) {
                    element = new VRIframeAgentGhost(index, null, normalizedObj.iframe_value, x, y, _Z, model);
                } else {
                    element = new VRIframeAgent(null, normalizedObj.iframe_value, x, y, _Z, model, normalizedObj);
                    element.scale.set(meshSize, meshSize, meshSize);
                    element.mesh.dbConfig = normalizedObj;
                    return element;
                }
                break;
            case 'iframeElement':
                return null;
            case 'timerElement':
                return null;
            case 'vapiUserCamera':
                element = new VRVapiCameraWithCSS3D({
                    model,
                    camera: null,
                    controls: null,
                    raycaster: null,
                    position: { x, y, z: _Z },
                    id: normalizedObj.id,
                    dbConfig: normalizedObj,
                    editMode: model?.editMode ?? false
                });
                break;
            case 'vapiTranscription':
                element = new VRVapiTranscriptionWithCSS3D(index, null, x, y, _Z, model);
                element.name = 'vapiTranscription';
                element.mesh.name = 'vapiTranscription';
                break;
            case 'profile':
                element = new VRProfileObject(index, null, texture, x, y, z, model);
                break;
            case 'chessGame':
                element = new VRChessObject(index, null, texture, x, y, z, model);
                break;
            case 'payPalDonation':
                element = new VRPayPalDonationObject(index, null, texture, x, y, z, model);
                break;
            case 'shopLink':
                element = new VRShopObject(index, null, texture, x, y, z, model);
                break;
            case 'infoElement':
                element = new VRInfoElement(index, null, texture, x, y, z, model);
                break;
            case 'inputFieldGuess':
                return null;
            case 'inputField':
                return null;
            case 'vrInvisibleAction':
                element = new VRInvisibleAction(index, null, texture, x, y, z, model);
                break;
            case 'embeddedWebpage':
                element = new VREmbeddedWebpageObject(index, null, texture, x, y, z, model);
                break;
            case 'tourScreenShare':
                element = new VRTourScreenShare(index, null, texture, x, y, z, model);
                break;
            case 'imageGallery':
                element = new VRImageGallery(index, null, texture, x, y, z, model);
                break;
            case 'videoGallery':
                element = new VRVideoGallery(index, null, texture, x, y, z, model);
                break;
            case '3dmodel':
                element = new VRModel(index, null, texture, x, y, z, model);
                break;
            case 'videoPlayer':
                element = new VRVideoPlayer(index, null, texture, x, y, z, model);
                break;
            case 'videoPlayerWebGL':
                element = new VRVideoPlayerWebGL(index, null, texture, x, y, z, model);
                break;
            case 'vrMusicObject':
                element = new VRMusicObject(index, null, texture, x, y, z, model);
                break;
            default:
                console.log('[VRPlaceObject] Unknown object type, skipping', { type, id });
                console.warn(`[VRPlaceObject] Skipping unknown object type "${type}"`, id);
                return null;
        }

        if (!element?.mesh) {
            console.log('[VRPlaceObject] Object has no mesh, skipping', { type, id });
            console.warn(`[VRPlaceObject] Failed to create object type "${type}"`, id);
            return null;
        }

        element.setId(id);
        element.setDbConfig(normalizedObj);
        element.setInitRotations();
        element.mesh.scale.set(meshSize, meshSize, meshSize);
        element.addMenuPosition();
        return element;
    }
}

export { VRPlaceObject }